import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import * as Switch from '@radix-ui/react-switch'
import {
  FolderOpen,
  Camera,
  Eye,
  ImageOff,
  Plus,
  ChevronRight,
  Loader2,
  Share2,
  UserPlus,
  X,
  Users,
  Trash2,
  SlidersHorizontal,
  ListFilter,
  ScanSearch,
  AudioLines,
  Siren,
  Map as MapIcon,
  Smartphone,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { GroupMembersManager } from '@/components/cameras/GroupMembersManager'
import { GroupBulkSettingsDialog } from '@/components/groups/GroupBulkSettingsDialog'
import { GroupDefaultViewDialog } from '@/components/groups/GroupDefaultViewDialog'
import { GroupDetectionDialog } from '@/components/groups/GroupDetectionDialog'
import { GroupAudioDialog } from '@/components/groups/GroupAudioDialog'
import { GroupCriticalDialog } from '@/components/groups/GroupCriticalDialog'
import { GroupSitePlanDialog } from '@/components/groups/GroupSitePlanDialog'
import { GroupMyPushDialog } from '@/components/groups/GroupMyPushDialog'
import { cn, formatCardDateTime } from '@/lib/utils'
import { masterPasswordRefusal } from '@/lib/deleteProtection'
import { cameraGroupsApi, camerasApi } from '@/api/endpoints'
import { toast } from '@/lib/toast'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { useAuthStore } from '@/store/authStore'
import type { CameraGroup } from '@/types'

// Single thumbnail tile with skeleton placeholder until loaded.
// `loading="lazy"` defers off-screen image fetches until the card scrolls in
// (#036 — meaningful improvement when a user has many groups).
function ThumbnailTile({ src }: { src: string | null }) {
  const [loaded, setLoaded] = useState(false)
  if (!src) {
    return (
      <div className="flex-1 relative bg-muted overflow-hidden h-full flex items-center justify-center">
        <ImageOff className="h-4 w-4 text-muted-foreground/25" />
      </div>
    )
  }
  return (
    <div className="flex-1 relative bg-muted overflow-hidden">
      {/* Skeleton: shimmer placeholder visible until the image paints. */}
      {!loaded && <div className="absolute inset-0 bg-muted animate-pulse" />}
      <img
        src={src}
        alt=""
        loading="lazy"
        className={cn(
          'h-full w-full object-cover transition-opacity duration-200',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
          setLoaded(true)  // hide the shimmer; img is gone, the bg shows through
        }}
      />
    </div>
  )
}

// Thumbnail strip — shows up to 3 latest detection images for cameras in a group
function ThumbnailStrip({ thumbnails }: { thumbnails: (string | null)[] }) {
  const shown = thumbnails.slice(0, 3)
  return (
    <div className="flex gap-0.5 h-28 overflow-hidden">
      {shown.map((src, i) => (
        <ThumbnailTile key={i} src={src} />
      ))}
      {shown.length === 0 && (
        <div className="flex-1 flex items-center justify-center bg-muted">
          <Camera className="h-6 w-6 text-muted-foreground/25" />
        </div>
      )}
    </div>
  )
}

function GroupCard({ group, onClick, onShare, onManage, onBulkSettings, onDefaultView, onDetection, onAudio, onCritical, onSitePlan, onMyPush }: { group: CameraGroup; onClick: () => void; onShare?: () => void; onManage?: () => void; onBulkSettings?: () => void; onDefaultView?: () => void; onDetection?: () => void; onAudio?: () => void; onCritical?: () => void; onSitePlan?: () => void; onMyPush?: () => void }) {
  // #152 — a superuser may manage and re-share ANY group, not just their own.
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser)
  const canManage = !!isSuperuser || group.is_owner !== false
  const { t } = useTranslation('groups')
  const cameras = group.cameras_details
  const thumbnails = cameras
    .filter((c) => c.latest_detection_thumbnail)
    .slice(0, 3)
    .map((c) => c.latest_detection_thumbnail)
  const totalDetections = cameras.reduce((sum, c) => sum + c.total_detections, 0)
  // Latest detection across any camera in the group (#037 cont.)
  const latestDetection = cameras
    .map((c) => c.last_detection_timestamp)
    .filter((t): t is string => Boolean(t))
    .sort()
    .at(-1)

  return (
    <Card
      className="overflow-hidden cursor-pointer hover:ring-1 hover:ring-primary/40 transition-all duration-150 group"
      onClick={onClick}
    >
      <ThumbnailStrip thumbnails={thumbnails} />
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
            <CardTitle className="text-body font-semibold truncate">{group.name}</CardTitle>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {/* My phone alerts for this group — ANYONE who can see the group (CP 391): it sets only the viewer's own
                alerts, on the member cameras they are a user of, so it is not behind the owner gate below */}
            {onMyPush && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onMyPush() }}
                disabled={group.camera_count === 0}
                title={group.camera_count === 0 ? t('myPush.empty') : t('myPush.button')}
                aria-label={t('myPush.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground/70"
              >
                <Smartphone className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Manage cameras — owner, or any superuser (#104, #152) */}
            {canManage && onManage && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onManage() }}
                title={t('manageCameras')}
                aria-label={t('manageCameras')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Share — owner, or any superuser (recipients still 403) (#096, #152) */}
            {canManage && onShare && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onShare() }}
                title={t('share.button')}
                aria-label={t('share.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors"
              >
                <Share2 className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Default events view for the group's cameras — owner, or any superuser (#238);
                the server changes only cameras the user may edit and reports the rest */}
            {canManage && onDefaultView && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDefaultView() }}
                disabled={group.camera_count === 0}
                title={group.camera_count === 0 ? t('defaultView.empty') : t('defaultView.button')}
                aria-label={t('defaultView.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground/70"
              >
                <ListFilter className="h-3.5 w-3.5" />
              </button>
            )}
            {/* An add-on detection model (Train Detection) for the group's cameras — owner, or any
                superuser (#266); the server changes only cameras the user may edit */}
            {canManage && onDetection && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDetection() }}
                disabled={group.camera_count === 0}
                title={group.camera_count === 0 ? t('detection.empty') : t('detection.button')}
                aria-label={t('detection.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground/70"
              >
                <ScanSearch className="h-3.5 w-3.5" />
              </button>
            )}
            {/* A sound-detection class (and its threshold) for the group's cameras — superusers only (#280),
                the rule of the per-camera sound settings */}
            {isSuperuser && onAudio && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAudio() }}
                disabled={group.camera_count === 0}
                title={group.camera_count === 0 ? t('audio.empty') : t('audio.button')}
                aria-label={t('audio.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground/70"
              >
                <AudioLines className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Critical alerts for the group (spray sound) — superusers only (#294). Open on an empty group
                too: the setting lives on the group, so cameras added later follow it */}
            {isSuperuser && onCritical && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCritical() }}
                title={t('critical.button')}
                aria-label={t('critical.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors"
              >
                <Siren className="h-3.5 w-3.5" />
              </button>
            )}
            {/* The group's site map (a drawing members switch to in the map window) — superusers only (#363), like
                Critical alerts; open on an empty group too */}
            {isSuperuser && onSitePlan && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onSitePlan() }}
                title={t('sitePlan.button')}
                aria-label={t('sitePlan.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors"
              >
                <MapIcon className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Apply settings to every camera in the group — superusers only (#226) */}
            {isSuperuser && onBulkSettings && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onBulkSettings() }}
                disabled={group.camera_count === 0}
                title={group.camera_count === 0 ? t('bulkSettings.empty') : t('bulkSettings.button')}
                aria-label={t('bulkSettings.button')}
                className="rounded-md p-1 text-muted-foreground/70 hover:text-primary hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground/70"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
              </button>
            )}
            <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
          </div>
        </div>
        {/* Recipient badge (#096) */}
        {group.is_owner === false && (
          <div className="mt-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-micro font-medium text-primary">
              <Share2 className="h-3 w-3" />
              {t('sharedBadge')}
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-0.5">
            <span className="text-micro text-muted-foreground">{t('stats.cameras')}</span>
            <p className="text-xs font-semibold">{group.camera_count}</p>
          </div>
          <div className="space-y-0.5">
            <span className="text-micro text-muted-foreground">{t('stats.active')}</span>
            <p className="text-xs font-semibold">{cameras.length}</p>
          </div>
          <div className="space-y-0.5">
            <span className="text-micro text-muted-foreground">{t('stats.detections')}</span>
            <p className="text-xs font-semibold flex items-center gap-1">
              <Eye className="h-3 w-3 text-muted-foreground" />
              {totalDetections}
            </p>
          </div>
        </div>

        {/* Camera name list preview */}
        {cameras.length > 0 && (
          <div className="text-xs text-muted-foreground truncate">
            {cameras
              .slice(0, 3)
              .map((c) => c.name)
              .join(', ')}
            {cameras.length > 3 && ` ${t('more', { count: cameras.length - 3 })}`}
          </div>
        )}

        {/* Latest detection timestamp, exact (item 371) */}
        {latestDetection && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Eye className="h-3 w-3 shrink-0" />
            <span className="truncate">Last detection {formatCardDateTime(latestDetection)}</span>
          </div>
        )}

        {/* View cameras link */}
        <div className="flex items-center gap-1 text-xs font-medium text-primary group-hover:text-primary/80 transition-colors pt-1">
          <Camera className="h-3 w-3" />
          {t('viewCameras')}
          <ChevronRight className="h-3 w-3 ml-auto" />
        </div>
      </CardContent>
    </Card>
  )
}

function UngroupedCard({ count, onClick }: { count: number; onClick: () => void }) {
  const { t } = useTranslation('groups')
  return (
    <Card
      className="overflow-hidden cursor-pointer hover:ring-1 hover:ring-muted-foreground/30 transition-all duration-150 group border-dashed"
      onClick={onClick}
    >
      <div className="h-20 bg-muted/30 flex items-center justify-center">
        <Camera className="h-8 w-8 text-muted-foreground/20" />
      </div>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="h-4 w-4 text-muted-foreground/50 shrink-0" />
            <CardTitle className="text-base text-muted-foreground">{t('ungrouped')}</CardTitle>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60 shrink-0 transition-colors" />
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <p className="text-xs text-muted-foreground">
          {t('ungroupedCount', { count })}
        </p>
      </CardContent>
    </Card>
  )
}

// Empty group card (#103) — owner-only. A group with no cameras can't show
// thumbnails or stats, so it gets a compact, dashed "needs attention" card with
// a clear path to add cameras and a quick delete for strays.
function EmptyGroupCard({
  group,
  onAddCameras,
  onDelete,
}: {
  group: CameraGroup
  onAddCameras: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation('groups')
  return (
    <Card className="overflow-hidden border-dashed bg-muted/20 transition-all duration-150">
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="h-4 w-4 text-muted-foreground/60 shrink-0" />
            <CardTitle className="text-body font-semibold truncate">{group.name}</CardTitle>
          </div>
          <button
            type="button"
            onClick={onDelete}
            title={t('needsCameras.delete')}
            aria-label={t('needsCameras.delete')}
            className="rounded-md p-1 text-muted-foreground/70 hover:text-destructive hover:bg-muted transition-colors shrink-0"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        <p className="text-xs text-muted-foreground">{t('needsCameras.cardHint')}</p>
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-full gap-1.5 text-xs"
          onClick={onAddCameras}
        >
          <Camera className="h-3.5 w-3.5" />
          {t('needsCameras.addCameras')}
        </Button>
      </CardContent>
    </Card>
  )
}

function GroupCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="h-20 shimmer" />
      <CardHeader className="pb-2">
        <div className="shimmer h-5 w-32 rounded" />
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <div className="shimmer h-8 rounded" />
          <div className="shimmer h-8 rounded" />
          <div className="shimmer h-8 rounded" />
        </div>
        <div className="shimmer h-4 w-40 rounded" />
      </CardContent>
    </Card>
  )
}

// #410 — one labelled Radix switch with a one-line help text under it.
function ShareTickSwitch({ id, testId, label, help, checked, onChange, disabled }: {
  id: string; testId?: string; label: string; help: string
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <label htmlFor={id} className="text-xs font-medium cursor-pointer">{label}</label>
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">{help}</p>
      </div>
      <Switch.Root
        id={id}
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-describedby={`${id}-hint`}
        className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30"
      >
        <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
      </Switch.Root>
    </div>
  )
}

// Share-group dialog (#096) — owner shares a group by email; recipients get
// view access + push by default; item 410's two switches can add delete / settings rights. Reads `group.shared_with` from the (live) group
// passed in; mutations invalidate the groups query so the list refreshes.
function ShareGroupDialog({ group, onClose }: { group: CameraGroup | null; onClose: () => void }) {
  const { t } = useTranslation('groups')
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [addDelete, setAddDelete] = useState(false)   // #410 — the add form's ticks, off by default
  const [addSettings, setAddSettings] = useState(false)

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })

  const shareMutation = useMutation({
    mutationFn: ({ id, email }: { id: number; email: string }) =>
      cameraGroupsApi.shareGroup(id, email, { can_delete_events: addDelete, can_change_settings: addSettings }),
    onSuccess: () => {
      toast.success(t('share.shared'))
      setEmail('')
      setAddDelete(false)
      setAddSettings(false)
      invalidate()
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast.error(t('share.shareFailed'), msg)
    },
  })

  const unshareMutation = useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) => cameraGroupsApi.unshareGroup(id, userId),
    onSuccess: () => {
      toast.success(t('share.removed'))
      invalidate()
    },
    onError: () => toast.error(t('share.removeFailed')),
  })

  // #410 — change one tick: optimistic on the groups query cache, rolled back with a toast if the server refuses.
  const permissionMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { user_id: number; can_delete_events?: boolean; can_change_settings?: boolean } }) =>
      cameraGroupsApi.updateGroupSharePermissions(id, body),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.cameraGroups.all() })
      const previous = queryClient.getQueriesData<CameraGroup[]>({ queryKey: queryKeys.cameraGroups.all() })
      queryClient.setQueriesData<CameraGroup[]>({ queryKey: queryKeys.cameraGroups.all() }, (old) =>
        Array.isArray(old)
          ? old.map((g) => g.id !== id ? g : {
              ...g,
              shared_with: g.shared_with?.map((u) => u.id !== body.user_id ? u : {
                ...u,
                ...(body.can_delete_events !== undefined && { can_delete_events: body.can_delete_events }),
                ...(body.can_change_settings !== undefined && { can_change_settings: body.can_change_settings }),
              }),
            })
          : old)
      return { previous }
    },
    onError: (err: unknown, _vars, ctx) => {
      ctx?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data))
      const data = (err as { response?: { data?: { detail?: string; error?: string } } })?.response?.data
      toast.error(t('share.permissionFailed'), data?.detail ?? data?.error)
    },
    onSettled: invalidate,
  })

  const sharedWith = group?.shared_with ?? []
  const trimmed = email.trim()

  return (
    <Dialog.Root open={group !== null} onOpenChange={(o) => { if (!o) { setEmail(''); setAddDelete(false); setAddSettings(false); onClose() } }}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ animationDuration: '200ms' }}
        />
        <Dialog.Content
          data-testid="share-group-dialog"
          // #410: two switches per person make the list long; the dialog scrolls inside the screen (360x640, six people)
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ animationDuration: '200ms' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 shrink-0">
                <Share2 className="h-4 w-4 text-primary" />
              </div>
              <Dialog.Title className="text-base font-semibold truncate">
                {t('share.title', { name: group?.name ?? '' })}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t('share.description')}</p>

          {/* Share by email */}
          <form
            className="mt-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (group && trimmed) shareMutation.mutate({ id: group.id, email: trimmed })
            }}
          >
            <div className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('share.emailPlaceholder')}
                className="h-9 text-sm"
                autoFocus
              />
              <Button type="submit" size="sm" className="h-9 gap-1.5 shrink-0" disabled={shareMutation.isPending || !trimmed}>
                {shareMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                {t('share.shareAction')}
              </Button>
            </div>
            <ShareTickSwitch
              id="share-add-delete" testId="add-can-delete-events"
              label={t('share.canDeleteEvents')} help={t('share.canDeleteEventsHelp')}
              checked={addDelete} onChange={setAddDelete}
            />
            <ShareTickSwitch
              id="share-add-settings" testId="add-can-change-settings"
              label={t('share.canChangeSettings')} help={t('share.canChangeSettingsHelp')}
              checked={addSettings} onChange={setAddSettings}
            />
          </form>

          {/* Current members */}
          <div className="mt-5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <Users className="h-3.5 w-3.5" />
              {t('share.sharedWithLabel')}
            </div>
            {sharedWith.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('share.noMembers')}</p>
            ) : (
              <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                {sharedWith.map((u) => (
                  <li key={u.id} className="rounded-lg border border-border px-3 py-2 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{u.username}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-destructive hover:text-destructive shrink-0"
                        onClick={() => group && unshareMutation.mutate({ id: group.id, userId: u.id })}
                        disabled={unshareMutation.isPending}
                      >
                        {t('share.remove')}
                      </Button>
                    </div>
                    <ShareTickSwitch
                      id={`share-${u.id}-delete`}
                      label={t('share.canDeleteEvents')} help={t('share.canDeleteEventsHelp')}
                      checked={!!u.can_delete_events}
                      onChange={(v) => group && permissionMutation.mutate({ id: group.id, body: { user_id: u.id, can_delete_events: v } })}
                    />
                    <ShareTickSwitch
                      id={`share-${u.id}-settings`}
                      label={t('share.canChangeSettings')} help={t('share.canChangeSettingsHelp')}
                      checked={!!u.can_change_settings}
                      onChange={(v) => group && permissionMutation.mutate({ id: group.id, body: { user_id: u.id, can_change_settings: v } })}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end pt-5">
            <Dialog.Close asChild>
              <Button variant="outline" size="sm">{t('share.close')}</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function GroupsPage() {
  const { t } = useTranslation('groups')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [newGroupName, setNewGroupName] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [shareGroupId, setShareGroupId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CameraGroup | null>(null)
  // #373 — the delete carries the master password typed in the dialog (tc = common strings, above).
  const [masterPassword, setMasterPassword] = useState('')
  const masterPasswordRef = useRef('')
  const [masterPasswordError, setMasterPasswordError] = useState<string | null>(null)
  const typeMasterPassword = (v: string) => { masterPasswordRef.current = v; setMasterPassword(v); setMasterPasswordError(null) }
  const resetMasterPassword = () => { masterPasswordRef.current = ''; setMasterPassword(''); setMasterPasswordError(null) }
  const [manageGroupId, setManageGroupId] = useState<number | null>(null)
  const [bulkGroup, setBulkGroup] = useState<CameraGroup | null>(null)
  const [defaultViewGroup, setDefaultViewGroup] = useState<CameraGroup | null>(null)
  const [detectionGroup, setDetectionGroup] = useState<CameraGroup | null>(null)
  const [audioGroup, setAudioGroup] = useState<CameraGroup | null>(null)
  const [criticalGroup, setCriticalGroup] = useState<CameraGroup | null>(null)
  const [sitePlanGroup, setSitePlanGroup] = useState<CameraGroup | null>(null)
  const [myPushGroup, setMyPushGroup] = useState<CameraGroup | null>(null)   // CP 391

  const createGroupMutation = useMutation({
    mutationFn: (name: string) => cameraGroupsApi.create({ name }),
    onSuccess: () => {
      toast.success(t('groupCreated'))
      setNewGroupName('')
      setShowCreateForm(false)
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
    },
    // Surface the server's field error (e.g. duplicate name) rather than a
    // generic message (#103) — the backend returns a friendly string under `name`.
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { name?: string[]; detail?: string } } })
        ?.response?.data
      toast.error(t('createFailed'), data?.name?.[0] ?? data?.detail)
    },
  })

  const deleteGroupMutation = useMutation({
    mutationFn: (id: number) => cameraGroupsApi.delete(id, masterPasswordRef.current),  // #373
    onSuccess: () => {
      toast.success(t('needsCameras.deleted'))
      setDeleteTarget(null)
      resetMasterPassword()
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
    },
    onError: (err: unknown) => {
      const refusal = masterPasswordRefusal(err)
      if (refusal) {  // #373 — the dialog stays open with the server's sentence
        setMasterPasswordError(refusal.code === 'master_password_locked' && refusal.retryAfter
          ? `${refusal.detail} ${tc('deleteProtection.retryIn', { seconds: refusal.retryAfter })}`
          : refusal.detail || tc('deleteProtection.refused'))
        return
      }
      toast.error(t('needsCameras.deleteFailed'))
    },
  })

  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: queryKeys.cameraGroups.all(),
    queryFn: () => cameraGroupsApi.getAll(),
    ...queryOptions.cameraGroups.all,
  })

  const { data: cameras = [] } = useQuery({
    queryKey: ['cameras', 'all', ''],
    queryFn: () => camerasApi.getAll(),
    ...queryOptions.cameras.all,
  })

  // Page is "ready" as soon as groups have loaded. The cameras query is
  // only needed for the ungrouped-card count (#036) — gating the whole
  // skeleton on it adds 2-4s of perceived load with no visible benefit
  // because /api/cameras/ pulls the full fleet (~250 cameras for some users).
  // The ungrouped card simply pops in when its data arrives.
  const isLoading = groupsLoading
  const ungroupedCount = cameras.filter((c) => c.group_id === null).length
  // Derive from live query data (not a snapshot) so the dialog's shared_with
  // list refreshes after share/unshare invalidates the query (#096).
  const shareGroup = groups.find((g) => g.id === shareGroupId) ?? null
  // Live group for the members manager (#104) — looked up by id so member rows
  // refresh after add/remove invalidates the query.
  const manageGroup = groups.find((g) => g.id === manageGroupId) ?? null

  // Empty groups the user OWNS surface in a pinned "needs cameras" section,
  // newest first, so a just-created group is immediately visible and can be
  // populated or removed (#103). Empty groups shared TO the user are read-only,
  // so they stay in the normal grid. created_at is an ISO string, so a
  // lexicographic compare is chronological.
  const isEmptyOwned = (g: CameraGroup) => g.camera_count === 0 && g.is_owner !== false
  const emptyOwnedGroups = groups
    .filter(isEmptyOwned)
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
  const mainGroups = groups.filter((g) => !isEmptyOwned(g))

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-up">
        <div className="min-w-0">
          {/* #126 — nav bar shows "Groups"; drop the redundant title + subtitle. Show the
              dynamic count as a bold h2, styled identically to the Events header.
              #129 — skeleton while loading so the count doesn't pop in (layout shift). */}
          {isLoading ? (
            <div className="h-7 sm:h-8 w-28 rounded-md shimmer" aria-hidden="true" />
          ) : (
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight truncate">
              {t('headerCount', { count: groups.length })}
            </h2>
          )}
        </div>
        <Button
          className="w-full sm:w-auto"
          onClick={() => setShowCreateForm(f => !f)}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('createGroup')}
        </Button>
      </div>

      {/* Inline create group form */}
      {showCreateForm && (
        <div className="flex gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <Input
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder={t('groupNamePlaceholder')}
            className="h-9 text-sm max-w-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newGroupName.trim()) createGroupMutation.mutate(newGroupName.trim())
              if (e.key === 'Escape') { setNewGroupName(''); setShowCreateForm(false) }
            }}
            autoFocus
          />
          <Button
            size="sm"
            className="h-9 text-xs gap-1.5"
            onClick={() => {
              if (newGroupName.trim()) createGroupMutation.mutate(newGroupName.trim())
            }}
            disabled={createGroupMutation.isPending || !newGroupName.trim()}
          >
            {createGroupMutation.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Plus className="h-3.5 w-3.5" />
            }
            {tc('actions.create')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-xs"
            onClick={() => { setNewGroupName(''); setShowCreateForm(false) }}
          >
            {tc('actions.cancel')}
          </Button>
        </div>
      )}

      {/* Skeletons */}
      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => <GroupCardSkeleton key={i} />)}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && groups.length === 0 && (
        <Card>
          <CardContent className="flex h-64 flex-col items-center justify-center text-center gap-4">
            <FolderOpen className="h-12 w-12 text-muted-foreground/30" />
            <div>
              <h3 className="text-lg font-semibold">{t('empty.title')}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('empty.message')}
              </p>
            </div>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('createGroup')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Group cards */}
      {!isLoading && groups.length > 0 && (
        <>
          {/* "Needs cameras" — empty owned groups, pinned to the top (#103) */}
          {emptyOwnedGroups.length > 0 && (
            <section className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <span className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary text-micro font-medium h-5 min-w-5 px-1.5">
                    {emptyOwnedGroups.length}
                  </span>
                  {t('needsCameras.title')}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">{t('needsCameras.hint')}</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {emptyOwnedGroups.map((group) => (
                  <EmptyGroupCard
                    key={group.id}
                    group={group}
                    onAddCameras={() => setManageGroupId(group.id)}
                    onDelete={() => { resetMasterPassword(); setDeleteTarget(group) }}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Separator — only when both sections are present */}
          {emptyOwnedGroups.length > 0 && mainGroups.length > 0 && (
            <div className="flex items-center gap-3" aria-hidden="true">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground">{t('sectionYourGroups')}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          {/* Populated groups (+ ungrouped card) */}
          {(mainGroups.length > 0 || ungroupedCount > 0) && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {mainGroups.map((group) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  onClick={() =>
                    navigate(`/cameras?group=${group.id}`, {
                      state: { from: { pathname: '/groups', search: '', labelKey: 'nav.groups' } },
                    })
                  }
                  onShare={() => setShareGroupId(group.id)}
                  onManage={() => setManageGroupId(group.id)}
                  onBulkSettings={() => setBulkGroup(group)}
                  onDefaultView={() => setDefaultViewGroup(group)}
                  onDetection={() => setDetectionGroup(group)}
                  onAudio={() => setAudioGroup(group)}
                  onCritical={() => setCriticalGroup(group)}
                  onSitePlan={() => setSitePlanGroup(group)}
                  onMyPush={() => setMyPushGroup(group)}
                />
              ))}

              {/* Ungrouped card — only show if there are ungrouped cameras */}
              {ungroupedCount > 0 && (
                <UngroupedCard
                  count={ungroupedCount}
                  onClick={() =>
                    navigate('/cameras?group=ungrouped', {
                      state: { from: { pathname: '/groups', search: '', labelKey: 'nav.groups' } },
                    })
                  }
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Share-group dialog (#096) */}
      <ShareGroupDialog group={shareGroup} onClose={() => setShareGroupId(null)} />

      {/* Delete empty-group confirm (#103) — shared ConfirmDialog (locks while in flight) */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => { if (!o) { setDeleteTarget(null); resetMasterPassword() } }}
        title={t('needsCameras.deleteConfirmTitle', { name: deleteTarget?.name ?? '' })}
        description={t('needsCameras.deleteConfirmMessage')}
        confirmLabel={t('needsCameras.deleteConfirmAction')}
        variant="destructive"
        loading={deleteGroupMutation.isPending}
        onConfirm={() => deleteTarget && deleteGroupMutation.mutate(deleteTarget.id)}
        input={{ label: tc('deleteProtection.masterPassword'), value: masterPassword, onChange: typeMasterPassword, error: masterPasswordError }}
      />

      {/* Group members manager (#104) */}
      <GroupMembersManager group={manageGroup} onClose={() => setManageGroupId(null)} />

      {/* Default events view for a group's cameras (#238) — a fresh instance per group */}
      {defaultViewGroup && (
        <GroupDefaultViewDialog
          key={defaultViewGroup.id}
          group={defaultViewGroup}
          open
          onOpenChange={(o) => { if (!o) { const id = defaultViewGroup.id; setDefaultViewGroup((g) => (g?.id === id ? null : g)) } }}
        />
      )}

      {/* An add-on detection model for a group's cameras (#266) — a fresh instance per group */}
      {detectionGroup && (
        <GroupDetectionDialog
          key={detectionGroup.id}
          group={detectionGroup}
          open
          onOpenChange={(o) => { if (!o) { const id = detectionGroup.id; setDetectionGroup((g) => (g?.id === id ? null : g)) } }}
        />
      )}

      {/* A sound-detection class for a group's cameras (#280) — superusers only; a fresh instance per group */}
      {audioGroup && (
        <GroupAudioDialog
          key={audioGroup.id}
          group={audioGroup}
          open
          onOpenChange={(o) => { if (!o) { const id = audioGroup.id; setAudioGroup((g) => (g?.id === id ? null : g)) } }}
        />
      )}

      {/* Critical alerts for a group (#294) — superusers only; a fresh instance per group */}
      {criticalGroup && (
        <GroupCriticalDialog
          key={criticalGroup.id}
          group={criticalGroup}
          open
          onOpenChange={(o) => { if (!o) { const id = criticalGroup.id; setCriticalGroup((g) => (g?.id === id ? null : g)) } }}
        />
      )}

      {/* The group's site map (#363) — superusers only; a fresh instance per group */}
      {sitePlanGroup && (
        <GroupSitePlanDialog
          key={sitePlanGroup.id}
          group={sitePlanGroup}
          open
          onOpenChange={(o) => { if (!o) { const id = sitePlanGroup.id; setSitePlanGroup((g) => (g?.id === id ? null : g)) } }}
        />
      )}

      {/* My phone alerts for a group (CP 391) — anyone who can see the group; a fresh instance per group */}
      {myPushGroup && (
        <GroupMyPushDialog
          key={myPushGroup.id}
          group={myPushGroup}
          open
          onOpenChange={(o) => { if (!o) { const id = myPushGroup.id; setMyPushGroup((g) => (g?.id === id ? null : g)) } }}
        />
      )}

      {/* Bulk group settings (#226) — superusers only; closing does not cancel an apply */}
      {bulkGroup && (
        <GroupBulkSettingsDialog
          // A fresh instance per group: no state carries from one group to the next.
          key={bulkGroup.id}
          group={bulkGroup}
          open
          // Group-scoped: a late close from one group's dialog never closes another's.
          onOpenChange={(o) => { if (!o) { const id = bulkGroup.id; setBulkGroup((g) => (g?.id === id ? null : g)) } }}
        />
      )}
    </div>
  )
}
